import { useMemo } from "react";
import type {
  IeepaAnalysisEntry,
  IeepaAnalysisReport,
} from "@/types/ieepa";

export interface AnalyticsTile {
  key: string;
  label: string;
  entryCount: number;
  ieepaDuty: number;
  /** Sum of expectedRefund (paid − line) across the tile. */
  expectedRefund: number;
  tone: "success" | "warn" | "danger" | "neutral";
}

export interface SubBucket {
  count: number;
  ieepaDuty: number;
  expectedRefund: number;
}

export interface BucketSubSplit {
  clean: SubBucket;
  recon: SubBucket;
  protest: SubBucket;
  both: SubBucket;
}

export interface BucketSplits {
  CAPE: BucketSubSplit;
  PROTEST_RECOMMENDED: BucketSubSplit;
  OUT_OF_WINDOW: BucketSubSplit;
}

export interface CapeAnalyticsResult {
  tiles: AnalyticsTile[];
  /** CAPE bucket entries — combined pending + not-yet-claimed (excludes already-refunded). */
  capeEligibleEntries: IeepaAnalysisEntry[];
  /** Entries CBP has already refunded under CAPE (refundType === 'CAPE'). */
  capeCertifiedEntries: IeepaAnalysisEntry[];
  /** Subset of CAPE bucket: claim already filed (claimStatus set), awaiting re-liquidation. */
  capePendingEntries: IeepaAnalysisEntry[];
  /** Subset of capePendingEntries whose claimStatus indicates rejection by CBP. */
  capePendingRejectedEntries: IeepaAnalysisEntry[];
  /** Projected refund $ for the rejected subset (excludes existing-protest entries). */
  pendingRejectedRefund: number;
  /** Subset of CAPE bucket: no claim filed yet — Phase-1-eligible, refund still claimable. */
  capeEligibleOnlyEntries: IeepaAnalysisEntry[];
  /** Indicator split (clean / recon / protest / both) over capeEligibleOnlyEntries — drives the CAPE Eligible bar segments. */
  capeEligibleOnlySplit: BucketSubSplit;
  /** PROTEST_RECOMMENDED bucket — liquidated 81–180d, protest still filable. */
  protestRecommendedEntries: IeepaAnalysisEntry[];
  /** OUT_OF_WINDOW bucket — liquidated >180d, no recovery path. */
  outOfWindowEntries: IeepaAnalysisEntry[];
  /** Entries flagged with an existing protest — cross-cuts all buckets. */
  hasProtestEntries: IeepaAnalysisEntry[];
  /** Live refund estimate: IEEPA $ summed across the CAPE bucket (pending + eligible, excluding existing protests). */
  projectedRefund: number;
  /** Projected refund $ for CAPE Pending sub-bucket (claim filed, no existing protest). */
  pendingProjectedRefund: number;
  /** Projected refund $ for CAPE Eligible sub-bucket (no claim filed, no existing protest). */
  eligibleOnlyProjectedRefund: number;
  /**
   * Already-refunded $ from CBP (refundType === 'CAPE'). Pulled from
   * report.totals — read against the full book, not the IEEPA slice.
   */
  refundedAmount: number;
  refundedEntries: number;
  refundedInterestAmount: number;
  /** refundedAmount + projectedRefund — the hero number. */
  capePhase1TotalAmount: number;
  /** refundedEntries + capeEligibleEntries.length. */
  capePhase1TotalEntries: number;
  bucketSplits: BucketSplits;
  totalEntryCount: number; // CAPE + PROTEST_RECOMMENDED + OUT_OF_WINDOW (excludes UNKNOWN)
}

const sumDuty = (entries: IeepaAnalysisEntry[]): number =>
  entries.reduce((s, e) => s + (e.ieepaDuty || 0), 0);

/* ─── CAPE Pending/Eligible split helpers.
 * MUST stay byte-for-byte identical to the copies in
 * apps/api/src/services/ieepaAnalysis/artifacts.ts. The backend uses them
 * to build the downloaded workbook — any divergence makes the workbook
 * contradict the page. If you change one, change both. */

/**
 * "Has the broker filed a CAPE claim with CBP?" — claimStatus non-empty
 * means a claim was filed (any state: Accepted, In Review, Rejected, …).
 * Exported so the export dialog can apply the exact same predicate as
 * the CAPE Pending vs CAPE Eligible split on the page.
 */
export const hasFiledClaim = (
  status: string | null | undefined
): boolean =>
  typeof status === "string" && status.trim().length > 0;

/**
 * Best-available refund estimate per entry. expectedRefund (paid − line)
 * is preferred when measurable; otherwise fall back to ieepaDuty. Same
 * formula the page uses when summing the CAPE Eligible refund total.
 */
export const refundableAmount = (e: IeepaAnalysisEntry): number => {
  const expected = e.expectedRefund ?? 0;
  return expected > 0 ? expected : (e.ieepaDuty ?? 0);
};

/**
 * Empirically validated refund $ — `max(0, totalPaidDuty − nonIeepaLineDuty)`.
 * Falls back to ieepaDuty if expectedRefund is missing (older API responses
 * that don't carry the field).
 */
const sumExpectedRefund = (entries: IeepaAnalysisEntry[]): number =>
  entries.reduce(
    (s, e) => s + (e.expectedRefund ?? e.ieepaDuty ?? 0),
    0
  );

const emptySub = (): SubBucket => ({
  count: 0,
  ieepaDuty: 0,
  expectedRefund: 0,
});
const emptySplit = (): BucketSubSplit => ({
  clean: emptySub(),
  recon: emptySub(),
  protest: emptySub(),
  both: emptySub(),
});

const splitBucket = (entries: IeepaAnalysisEntry[]): BucketSubSplit => {
  const split = emptySplit();
  for (const e of entries) {
    const hasRecon = e.reconciliationIndicator === "Y";
    const hasProtest = e.hasExistingProtest === true;
    const key: keyof BucketSubSplit =
      hasRecon && hasProtest ? "both"
      : hasRecon ? "recon"
      : hasProtest ? "protest"
      : "clean";
    split[key].count += 1;
    split[key].ieepaDuty += e.ieepaDuty || 0;
    split[key].expectedRefund += e.expectedRefund ?? e.ieepaDuty ?? 0;
  }
  return split;
};

/** Pure computation — exported so the selector can be unit-tested without React. */
export const computeCapeAnalytics = (
  report: IeepaAnalysisReport | undefined
): CapeAnalyticsResult => {
  const entries = report?.entries ?? [];
  // CAPE Phase 1 bucket = liquidation-window-eligible AND not already
  // refunded. Excluding refundType==='CAPE' here prevents the hero from
  // double-counting an entry that already has its CBP refund recorded
  // (those entries flow into the hero via report.totals.capeRefunded*
  // instead). Without this filter, an entry whose Ch99DutyDict was
  // non-empty would land in BOTH the cape bucket and the refund rollup.
  const cape = entries.filter(
    (e) => e.bucket === "CAPE" && e.refundType !== "CAPE"
  );
  const capeCertified = entries.filter((e) => e.refundType === "CAPE");
  // Bucket split is claim-status-based, not amount-based:
  //   - Pending  = broker has filed a CAPE claim with CBP (claimStatus is
  //                non-empty — Accepted, Rejected, or any value). The entry
  //                is somewhere in the CBP pipeline awaiting a final outcome.
  //   - Eligible = no claim filed yet (claimStatus null/empty). The entry
  //                qualifies for CAPE but the broker hasn't taken action.
  // Both buckets only count entries with a positive refundable amount
  // (paid − line if measurable; ieepaDuty otherwise). An earlier amount-based
  // split misrouted books with IEEPA exposure but no claims filed (e.g.
  // Shalam Imports) — those entries belong in Eligible, not Pending.
  const capePending = cape.filter(
    (e) => hasFiledClaim(e.claimStatus) && refundableAmount(e) > 0
  );
  const capeEligibleOnly = cape.filter(
    (e) => !hasFiledClaim(e.claimStatus) && refundableAmount(e) > 0
  );
  const protestRecommended = entries.filter(
    (e) => e.bucket === "PROTEST_RECOMMENDED"
  );
  const outOfWindow = entries.filter((e) => e.bucket === "OUT_OF_WINDOW");
  const hasProtest = entries.filter((e) => e.hasExistingProtest);
  // Projected refund excludes entries with existing protests — counsel
  // handles those separately. The footnote + caveat strip in the UI
  // state this exclusion; keep this filter aligned with that claim.
  const capePendingWithoutProtest = capePending.filter((e) => !e.hasExistingProtest);
  const capeEligibleOnlyWithoutProtest = capeEligibleOnly.filter((e) => !e.hasExistingProtest);
  // CBP rejection signal — claim was filed and CBP responded "rejected".
  // Match case-insensitively on whole-word boundary to avoid catching
  // "non-rejected" or other adjacent terms. Derived from the no-protest
  // pending set so capePendingRejectedEntries.length and
  // pendingRejectedRefund describe the same entries — the alertSegment in
  // the funnel passes both as count + amount of one bar segment.
  const isRejectedClaim = (status: string | null | undefined): boolean =>
    typeof status === "string" && /\brejected\b/i.test(status);
  const capePendingRejected = capePendingWithoutProtest.filter((e) =>
    isRejectedClaim(e.claimStatus)
  );

  const tiles: AnalyticsTile[] = [
    {
      key: "CAPE",
      label: "CAPE Phase 1",
      entryCount: cape.length,
      ieepaDuty: sumDuty(cape),
      expectedRefund: sumExpectedRefund(cape),
      tone: "success",
    },
    {
      key: "PROTEST_RECOMMENDED",
      label: "Protest Recommended",
      entryCount: protestRecommended.length,
      ieepaDuty: sumDuty(protestRecommended),
      expectedRefund: sumExpectedRefund(protestRecommended),
      tone: "warn",
    },
    {
      key: "OUT_OF_WINDOW",
      label: "Out of Window",
      entryCount: outOfWindow.length,
      ieepaDuty: sumDuty(outOfWindow),
      expectedRefund: sumExpectedRefund(outOfWindow),
      tone: "danger",
    },
    {
      key: "HAS_PROTEST",
      label: "Has Protest (overlay)",
      entryCount: hasProtest.length,
      ieepaDuty: sumDuty(hasProtest),
      expectedRefund: sumExpectedRefund(hasProtest),
      tone: "danger",
    },
  ];

  const bucketSplits: BucketSplits = {
    CAPE: splitBucket(cape),
    PROTEST_RECOMMENDED: splitBucket(protestRecommended),
    OUT_OF_WINDOW: splitBucket(outOfWindow),
  };
  const capeEligibleOnlySplit = splitBucket(capeEligibleOnly);
  const totalEntryCount =
    cape.length + protestRecommended.length + outOfWindow.length;

  const refundedAmount = report?.totals?.capeRefundedAmount ?? 0;
  const refundedEntries = report?.totals?.capeRefundedEntries ?? 0;
  const refundedInterestAmount =
    report?.totals?.capeRefundedInterestAmount ?? 0;
  // Both buckets use the same per-entry $ formula (`refundableAmount`):
  // empirical paid − line when measurable, falling back to ieepaDuty.
  // Validated to the cent against the 9 CBP-certified Original Brands
  // entries via the paid − line identity.
  const pendingProjectedRefund = capePendingWithoutProtest.reduce(
    (s, e) => s + refundableAmount(e),
    0
  );
  const pendingRejectedRefund = capePendingRejected.reduce(
    (s, e) => s + refundableAmount(e),
    0
  );
  const eligibleOnlyProjectedRefund = capeEligibleOnlyWithoutProtest.reduce(
    (s, e) => s + refundableAmount(e),
    0
  );
  const projectedRefund = pendingProjectedRefund + eligibleOnlyProjectedRefund;

  return {
    tiles,
    capeEligibleEntries: cape,
    capeCertifiedEntries: capeCertified,
    capePendingEntries: capePending,
    capePendingRejectedEntries: capePendingRejected,
    pendingRejectedRefund,
    capeEligibleOnlyEntries: capeEligibleOnly,
    capeEligibleOnlySplit,
    protestRecommendedEntries: protestRecommended,
    outOfWindowEntries: outOfWindow,
    hasProtestEntries: hasProtest,
    projectedRefund,
    pendingProjectedRefund,
    eligibleOnlyProjectedRefund,
    refundedAmount,
    refundedEntries,
    refundedInterestAmount,
    capePhase1TotalAmount: refundedAmount + projectedRefund,
    capePhase1TotalEntries: refundedEntries + cape.length,
    bucketSplits,
    totalEntryCount,
  };
};

/**
 * Pure, memoized page selector. Surfaces bucket totals for the Overview
 * tiles and the per-tab EntryTable rows.
 *
 * No toggles — the export modal (ExportCapeReportDialog) is the single
 * place toggles live, so the page numbers always reflect the raw book.
 *
 * Bucket model (server-authoritative):
 *   - `CAPE`                — Phase 1 eligible (unliquidated OR ≤80d liquidated)
 *   - `PROTEST_RECOMMENDED` — liquidated 81–180d; formal protest still filable
 *   - `OUT_OF_WINDOW`       — liquidated >180d; past protest window, no recovery
 *   - `UNKNOWN`             — missing / unrecognized liquidation status
 */
export const useCapeAnalytics = (
  report: IeepaAnalysisReport | undefined
): CapeAnalyticsResult => {
  return useMemo(() => computeCapeAnalytics(report), [report]);
};
